import axios, { AxiosRequestConfig, AxiosResponse, CancelToken } from 'axios'
import { CommonFilterModel, CommonParams, ExportCommonParams } from '@/api/resources/common/shared/models/common.models'
import { IResponseAgGrid } from '@/shared/models/ag-grid.model'
import { Nullable } from '@/shared/types/app.types'
import {
    InvoicePaymentPatchRequest,
    InvoicePaymentResponse
} from '@/api/services/billing/invoice-payment/billing-invoice-payment.dto'
import {
    BillingBillableDeliveryNoteSearchGridRequest,
    BillingCaseSearchGridRequest,
    BillingPatientSearchGridRequest
} from '@/api/services/billing/billing.dto'
import { ReportGridRequest } from '@/api/services/common.dto'

class BillingInvoicePaymentService {
    async get(
        laboratoryId: number,
        invoicePaymentId: number,
        cancelToken?: CancelToken
    ): Promise<InvoicePaymentResponse> {
        const response =
            await axios.get<InvoicePaymentResponse>(
                `/billing/laboratories/${laboratoryId}/invoicePayments/${invoicePaymentId}`,
                { cancelToken })
        return response.data
    }

    async patch(
        laboratoryId: number,
        request: InvoicePaymentPatchRequest
    ): Promise<void> {
        const response =
            await axios.patch<void>(
                `/billing/laboratories/${laboratoryId}/invoicePayments`,
                request)
        return response.data
    }

    async createFromInvoice(
        laboratoryId: number,
        invoiceId: number
    ): Promise<InvoicePaymentResponse> {
        const response =
            await axios.post<InvoicePaymentResponse>(
                `/billing/laboratories/${laboratoryId}/invoicePayments/invoice`,
                invoiceId,
                { headers: { 'Content-Type': 'application/json' } })
        return response.data
    }
    async createFromAppliedBillableConcepts(
        laboratoryId: number,
        appliedBillableConceptIds: number[]
    ): Promise<InvoicePaymentResponse> {
        const response =
            await axios.post<InvoicePaymentResponse>(
                `/billing/laboratories/${laboratoryId}/invoicePayments/appliedBillableConcepts`,
                appliedBillableConceptIds)
        return response.data
    }

    async paginator(
        filterModel: Nullable<CommonFilterModel>,
        params: Nullable<CommonParams>,
        laboratoryId: number
    ): Promise<IResponseAgGrid<InvoicePaymentResponse>> {
        const response =
            await axios.put<IResponseAgGrid<InvoicePaymentResponse>>(
                `/billing/laboratories/${laboratoryId}/invoicePayments`,
                { ...filterModel },
                { params })
        return response.data
    }
    async export(
        laboratoryId: number,
        gridColumns?: ReportGridRequest,
        params?: ExportCommonParams
    ): Promise<AxiosResponse<Blob>> {
        return axios.put<Blob>(
            `/billing/laboratories/${laboratoryId}/invoicePayments/export`,
            { ...gridColumns },
            { params, responseType: 'arraybuffer' })
    }

    async searchPatient(
        laboratoryId: number,
        request: BillingPatientSearchGridRequest,
        params?: CommonParams
    ): Promise<IResponseAgGrid<InvoicePaymentResponse>> {
        const response =
            await axios.put<IResponseAgGrid<InvoicePaymentResponse>>(
                `/billing/laboratories/${laboratoryId}/invoicePayments/patient`,
                request,
                { params })
        return response.data
    }
    async searchCase(
        laboratoryId: number,
        request: BillingCaseSearchGridRequest,
        params?: CommonParams
    ): Promise<IResponseAgGrid<InvoicePaymentResponse>> {
        const response =
            await axios.put<IResponseAgGrid<InvoicePaymentResponse>>(
                `/billing/laboratories/${laboratoryId}/invoicePayments/case`,
                request,
                { params })
        return response.data
    }
    async searchBillableDeliveryNote(
        laboratoryId: number,
        request: BillingBillableDeliveryNoteSearchGridRequest,
        params?: CommonParams
    ): Promise<IResponseAgGrid<InvoicePaymentResponse>> {
        const response =
            await axios.put<IResponseAgGrid<InvoicePaymentResponse>>(
                `/billing/laboratories/${laboratoryId}/invoicePayments/billableDeliveryNote`,
                request,
                { params })
        return response.data
    }

    async addConcepts(
        laboratoryId: number,
        invoicePaymentId: number,
        appliedBillableConceptIds: number[]
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoicePayments/${invoicePaymentId}/addConcepts`,
                appliedBillableConceptIds)
        return response.data
    }
    async addAllConcepts(
        laboratoryId: number,
        invoicePaymentId: number
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoicePayments/${invoicePaymentId}/addAllConcepts`)
        return response.data
    }

    async removeConcepts(
        laboratoryId: number,
        invoicePaymentId: number,
        appliedBillableConceptIds: number[]
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoicePayments/${invoicePaymentId}/removeConcepts`,
                appliedBillableConceptIds)
        return response.data
    }
    async removeAllConcepts(
        laboratoryId: number,
        invoicePaymentId: number
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoicePayments/${invoicePaymentId}/removeAllConcepts`)
        return response.data
    }

    async verify(
        laboratoryId: number,
        invoicePaymentId: number
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoicePayments/${invoicePaymentId}/verify`)
        return response.data
    }
    async cancel(
        laboratoryId: number,
        invoicePaymentId: number
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoicePayments/${invoicePaymentId}/cancel`)
        return response.data
    }

    async getReport(
        laboratoryId: number,
        invoicePaymentId: number,
        encoded: boolean
    ): Promise<Blob> {
        const config: AxiosRequestConfig = {
            params: { encoded },
            headers: { type: 'application/pdf' },
            responseType: encoded ? 'text' : 'arraybuffer'
        }
        const response =
            await axios.put<Blob>(
                `/billing/laboratories/${laboratoryId}/invoicePayments/${invoicePaymentId}/report`,
                undefined,
                config)
        return response.data
    }
    async replaceReport(
        laboratoryId: number,
        invoicePaymentId: number
    ): Promise<void> {
        const response =
            await axios.put<void>(
                `/billing/laboratories/${laboratoryId}/invoicePayments/${invoicePaymentId}/replaceReport`)
        return response.data
    }
}
export const billingInvoicePaymentService = new BillingInvoicePaymentService()
